import { Directive, ElementRef, Output, EventEmitter, NgZone, OnDestroy, Input } from '@angular/core';
import {auditTime, fromEvent, merge, Subscription} from 'rxjs';
import {getScrollParents} from "../_util/utils";

@Directive({
    selector: '[appPollMoveObserver]',
    standalone: false
})
export class PollMoveObserverDirective implements OnDestroy {
  @Output() moved = new EventEmitter<DOMRect>();
  @Output() outOfView = new EventEmitter<void>();

  @Input()
  set enabled(value: boolean) {

    if (value) this.subscribe();
    else this.ngOnDestroy();
  }

  private lastRect!: DOMRect;
  private sub = new Subscription();
  private intersectionObserver!: IntersectionObserver;

  constructor(
    private el: ElementRef<HTMLElement>,
    private ngZone: NgZone
  ) {}

  subscribe() {
    this.lastRect = this.el.nativeElement.getBoundingClientRect();

    this.ngZone.runOutsideAngular(() => {
      const scrollParents = getScrollParents(this.el.nativeElement);
      const scrollStreams = scrollParents.map(parent => fromEvent(parent, 'scroll'));
      const resizeStream = fromEvent(window, 'resize');

      this.sub = merge(...scrollStreams, resizeStream)
        .pipe(auditTime(10))
        .subscribe(() => this.checkPosition());
    });

    this.intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          this.outOfView.emit();
        }
      }
    }, {
      root: null,
      rootMargin: '0px',
      threshold: 0
    });

    this.intersectionObserver.observe(this.el.nativeElement);
  }

  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
  }

  private checkPosition() {
    const newRect = this.el.nativeElement.getBoundingClientRect();
    if (
      newRect.top !== this.lastRect.top ||
      newRect.left !== this.lastRect.left
    ) {
      this.lastRect = newRect;
      this.ngZone.run(() => this.moved.emit(newRect));
    }
  }
}
